// @ts-nocheck
import * as React from "react";
import { Ic, Sidebar, Topbar, AppFooter, AvatarImg, useCurrentUser, apiErrorMessage } from "./common";
import { api, elderly as elderlyApi, needsAssessments as needsApi } from "../api";
import { usePermissionsFromSession } from "../components/permissions";
import { ElderlyCreateModal } from "../components/ElderlyCreateModal";

// ─────────────────────────────────────────────────────────────────────────
// 어르신 상세 (데스크톱) — 권역 관리자 운영 시각.
//
// management.tsx(케어매니저 관리)와 동일한 UI 구조를 따른다:
//   • app height:100vh + shell overflow:hidden + .main 컨테이너
//   • 검색·필터 한 줄 카드 + 보조 칩 한 줄
//   • 메인 grid 1fr 380px (좌 테이블 + 우 상세 카드)
//   • 우측 카드: 그라데이션 헤더 + 큰 아바타 + KPI 그리드(2x3) + 액션 row
//
// 매니저 관리와 시각적 일관성을 보장하면서 어르신 도메인 정보(복약/매니저/최근 방문)는
// KPI 아래 별도 섹션으로 보존한다.
// ─────────────────────────────────────────────────────────────────────────

const RISK_LABEL = { critical: "위험", warning: "주의", stable: "안정" };
const RISK_COLOR = { critical: "rose",   warning: "amber",  stable: "green" };
const STATUS_LABEL = { available: "가용", busy: "바쁨", overloaded: "과부하", moving: "이동중", offline: "오프라인" };
const STATUS_COLOR = { available: "green", busy: "amber", overloaded: "rose", moving: "amber", offline: "gray" };
const VISIT_LABEL  = { planned: "예정", in_progress: "진행", completed: "완료", canceled: "취소" };
const VISIT_COLOR  = { planned: "sky",  in_progress: "amber", completed: "green", canceled: "gray" };

// 좌측 목록 페이지 크기(서버 elderlies#index PER_PAGE_MAX 이내).
const PER_PAGE = 50;

const SORT_OPTIONS = [
  { key: "risk",    label: "위험 높은 순" },
  { key: "name",    label: "이름 가나다 순" },
];

function ElderlyDetail() {
  const { user, loaded: userLoaded } = useCurrentUser();
  const perms = usePermissionsFromSession({ user, loaded: userLoaded });

  const [selectedId, setSelectedId] = React.useState(() => {
    const raw = new URLSearchParams(window.location.search).get("id");
    return raw ? Number(raw) : null;
  });

  const [list, setList] = React.useState([]);
  const [detail, setDetail] = React.useState(null);
  const [loading, setLoading] = React.useState(true);
  const [detailLoading, setDetailLoading] = React.useState(false);
  const [err, setErr] = React.useState(null);

  const [q, setQ] = React.useState("");
  // 위험등급 필터는 셀렉트와 칩이 같은 상태를 공유한다(단일 출처).
  // 과거엔 칩만 클라이언트 필터라, 서버가 위험순 상위 N건만 내려주는 구간에서
  // "주의/안정" 칩이 사실상 빈 목록을 만들었다(같은 라벨의 셀렉트는 정상 동작).
  const [riskFilter, setRiskFilter] = React.useState("all");
  // 서버 페이지네이션. 예전에는 bare 200건 상한이라, 권역 어르신이 그보다 많으면
  // 나머지는 어떤 필터로도 도달할 수 없었고 헤더의 "등록 200명"이 권역 총원처럼 읽혔다.
  const [page, setPage] = React.useState(1);
  const [pagination, setPagination] = React.useState({ page: 1, total: 0, total_pages: 1 });
  const [riskCounts, setRiskCounts] = React.useState({});
  const [sortKey, setSortKey] = React.useState("risk");
  const [creating, setCreating] = React.useState(false);
  // 등록 직후 발급된 연결코드 안내(보호자가 이 코드로 가입해 연결한다).
  const [created, setCreated] = React.useState(null);

  // 검색어는 서버로 보내므로 타이핑마다 치지 않도록 350ms 디바운스.
  const [dq, setDq] = React.useState("");
  React.useEffect(() => { const t = setTimeout(() => setDq(q.trim()), 350); return () => clearTimeout(t); }, [q]);

  // 좌측 목록 로드. 사용자가 자기 권역에 묶여 있으면 그 권역으로 한정.
  const load = React.useCallback(async () => {
    setLoading(true);
    try {
      const query = { page: String(page), per_page: String(PER_PAGE) };
      if (dq)                     query.q = dq;
      if (riskFilter !== "all")   query.risk = riskFilter;
      if (user?.region_id)        query.region_id = String(user.region_id);
      const data = await api.list("elderlies", query);
      // page 파라미터를 보내면 객체 응답. 배열이 오면(구버전 서버) 방어적으로 수용.
      const rows = Array.isArray(data) ? data : (data.elderlies || []);
      setList(rows);
      if (Array.isArray(data)) {
        setPagination({ page: 1, total: rows.length, total_pages: 1 });
        setRiskCounts({});
      } else {
        setPagination(data.pagination || { page: 1, total: 0, total_pages: 1 });
        setRiskCounts(data.counts?.by_risk || {});
      }
      if (!selectedId && rows.length > 0) setSelectedId(rows[0].id);
    } catch (e) {
      setErr(humanizeError(e));
    } finally {
      setLoading(false);
    }
  }, [dq, riskFilter, page, user?.region_id]);

  React.useEffect(() => { load(); }, [load]);

  // 검색·필터 변경 시 1페이지로 되돌린다(빈 페이지 방지).
  React.useEffect(() => { setPage(1); }, [dq, riskFilter]);
  React.useEffect(() => {
    if (pagination.total_pages > 0 && page > pagination.total_pages) setPage(pagination.total_pages);
  }, [pagination.total_pages, page]);

  // 우측 상세 로드.
  React.useEffect(() => {
    if (!selectedId) { setDetail(null); return; }
    let cancelled = false;
    setDetailLoading(true);
    api.get("elderlies", selectedId)
      .then((d) => { if (!cancelled) setDetail(d); })
      .catch((e) => { if (!cancelled) setErr(humanizeError(e)); })
      .finally(() => { if (!cancelled) setDetailLoading(false); });
    return () => { cancelled = true; };
  }, [selectedId]);

  // 위험등급 필터는 서버(load)에서 이미 적용됐다 — 여기서는 정렬만 바꾼다.
  const visible = React.useMemo(() => {
    if (sortKey !== "name") return list;   // "risk"는 백엔드 기본 정렬과 동일
    return [...list].sort((a, b) => (a.name || "").localeCompare(b.name || "", "ko"));
  }, [list, sortKey]);

  // 위험 인원은 '현재 페이지'가 아니라 필터 전체 기준이어야 한다 — 서버 facet
  // (위험등급 필터를 뺀 분포)을 우선 쓰고, 없으면 로드된 배열로 센다.
  const criticalCount = riskCounts.critical ?? list.filter((e) => e.risk_level === "critical").length;

  const selected = detail?.elderly && detail.elderly.id === selectedId ? detail : null;

  // 위험도 변경 성공 시 우측 상세 + 좌측 목록 배지를 재조회 없이 즉시 동기화.
  const onRiskChanged = React.useCallback((updated) => {
    const newLevel = updated?.elderly?.risk_level;
    const id = updated?.elderly?.id;
    if (!id || !newLevel) return;
    setDetail((d) => (d && d.elderly?.id === id ? { ...d, elderly: { ...d.elderly, risk_level: newLevel } } : d));
    setList((rows) => rows.map((e) => (e.id === id ? { ...e, risk_level: newLevel } : e)));
  }, []);

  // 건강상태 변경 성공 시 우측 상세를 재조회 없이 즉시 동기화(좌측 목록엔 건강상태 미표시).
  const onHealthChanged = React.useCallback((updated) => {
    const id = updated?.elderly?.id;
    if (!id) return;
    const newHealth = updated.elderly.health_status;
    setDetail((d) => (d && d.elderly?.id === id ? { ...d, elderly: { ...d.elderly, health_status: newHealth } } : d));
  }, []);

  return (
    <div className="app" style={{height:'100vh'}}>
      <div className="shell">
        <Sidebar role="region" active="elderly"/>
        <div style={{display:'flex', flexDirection:'column', overflow:'hidden'}}>
          <Topbar title="어르신 명단" sub={`등록 ${pagination.total.toLocaleString()}명 · 위험 ${criticalCount.toLocaleString()}명`}/>
          <div className="main">

            {err && <div className="card" style={{padding:'10px 14px', marginBottom:10, background:'var(--rose-50)', color:'var(--rose-500)', fontSize:12, fontWeight:600}}>{err}</div>}

            {/* 검색·필터 한 줄 카드 — management와 동일 구조 */}
            <div className="card" style={{padding:14, marginBottom:14, display:'flex', alignItems:'center', gap:10, flexWrap:'wrap'}}>
              <div className="searchbar" style={{flex:'0 0 240px', position:'relative'}}>
                <span style={{position:'absolute', left:12, top:'50%', transform:'translateY(-50%)', color:'var(--ink-3)'}}>{Ic.search}</span>
                <input className="input" style={{paddingLeft:36}} placeholder="이름·주소 검색" value={q} onChange={e=>setQ(e.target.value)}/>
              </div>
              <select className="select" value={riskFilter} onChange={e=>setRiskFilter(e.target.value)} style={{width:130}}>
                <option value="all">전체 위험등급</option>
                {Object.entries(RISK_LABEL).map(([k,v]) => <option key={k} value={k}>{v}</option>)}
              </select>
              <select className="select" value={sortKey} onChange={e=>setSortKey(e.target.value)} style={{width:160}}>
                {SORT_OPTIONS.map(s => <option key={s.key} value={s.key}>{s.label}</option>)}
              </select>
              <span className="muted" style={{fontSize:11.5, marginLeft:6}}>
                {pagination.total.toLocaleString()}명
                {pagination.total_pages > 1 && ` · 페이지 ${pagination.page}/${pagination.total_pages}`}
              </span>
              <div style={{flex:1}}/>
              {perms.canManageCases && (
                <button type="button" className="btn btn-primary btn-md" onClick={()=>setCreating(true)}>
                  {Ic.plus} 신규 등록
                </button>
              )}
              <a className="btn btn-ghost btn-md" href="/dashboard/region" style={{textDecoration:'none'}}>{Ic.arrow} 권역 대시보드</a>
            </div>

            {/* 등록 성공 안내 — 연결코드는 보호자 가입 시 1회 사용되고 소진된다. */}
            {created && (
              <div className="card" style={{padding:'12px 16px', marginBottom:12, background:'var(--green-50)', display:'flex', alignItems:'center', gap:10, flexWrap:'wrap'}}>
                <span style={{fontSize:12.5, fontWeight:700, color:'var(--green-600, #15803d)'}}>
                  ✓ {created.elderly?.name} 어르신을 명단에 등록했습니다.
                </span>
                {created.connect_code && (
                  <span style={{fontSize:12}}>
                    보호자 연결코드 <b className="mono" style={{padding:'2px 8px', background:'var(--surface)', borderRadius:6}}>{created.connect_code}</b>
                    <span className="muted" style={{marginLeft:6, fontSize:11}}>보호자가 회원가입 → “연결코드로 연결”에서 사용</span>
                  </span>
                )}
                <div style={{flex:1}}/>
                <button type="button" className="btn btn-ghost btn-sm" onClick={()=>setCreated(null)}>닫기</button>
              </div>
            )}

            {/* 보조 칩 (management의 자격 칩과 같은 위치) — 위험등급 빠른 필터 */}
            <div style={{padding:'0 4px 12px', display:'flex', gap:6, flexWrap:'wrap'}}>
              {[{l:'전체', v:'all'}, {l:'위험', v:'critical'}, {l:'주의', v:'warning'}, {l:'안정', v:'stable'}].map((c,i)=>{
                // 건수는 서버 facet(위험등급 필터를 뺀 분포) — 칩을 눌러도 다른 칩의
                // 숫자가 0으로 무너지지 않는다.
                const n = c.v === 'all'
                  ? Object.values(riskCounts).reduce((a, b) => a + b, 0)
                  : riskCounts[c.v];
                return (
                  <span key={i} role="button" tabIndex={0} aria-pressed={riskFilter===c.v}
                    onClick={()=>setRiskFilter(c.v)}
                    onKeyDown={(e)=>{ if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); setRiskFilter(c.v); } }}
                    style={{padding:'6px 12px', fontSize:12, fontWeight:600, borderRadius:999, background: riskFilter===c.v?'var(--orange-500)':'var(--bg-sub)', color: riskFilter===c.v?'#fff':'var(--ink-2)', cursor:'pointer'}}>
                    {c.l}{n != null && <span style={{marginLeft:5, opacity:0.75}}>{n.toLocaleString()}</span>}
                  </span>
                );
              })}
            </div>

            {/* 메인 그리드 — management와 동일 1fr 380px */}
            <div style={{display:'grid', gridTemplateColumns:'1fr 380px', gap:14}}>
              <div className="card" style={{overflow:'hidden'}}>
                <table className="tbl">
                  <thead><tr>
                    <th style={{paddingLeft:22}}>어르신</th>
                    <th>나이</th>
                    <th>위험등급</th>
                    <th>주소</th>
                  </tr></thead>
                  <tbody>
                    {loading && <tr><td colSpan={4} className="muted" style={{padding:30, textAlign:'center'}}>불러오는 중…</td></tr>}
                    {!loading && visible.length === 0 && (
                      <tr><td colSpan={4} className="muted" style={{padding:30, textAlign:'center'}}>표시할 어르신이 없습니다.</td></tr>
                    )}
                    {visible.map((e,i) => (
                      <tr key={e.id}
                        style={{background: selectedId===e.id?'var(--orange-50)':'transparent', cursor:'pointer'}}
                        onClick={()=>setSelectedId(e.id)}>
                        <td style={{paddingLeft:22}}>
                          <div style={{display:'flex', alignItems:'center', gap:10}}>
                            <div className={"avatar sm " + (i%2?'green':'')}>{e.name?.[0] ?? "?"}</div>
                            <div>
                              <div style={{fontWeight:700, fontSize:12.5}}>{e.name}</div>
                              <div className="muted" style={{fontSize:10}}>{e.age != null ? `${e.age}세` : ''}</div>
                            </div>
                          </div>
                        </td>
                        <td className="mono">{e.age != null ? `${e.age}` : '-'}</td>
                        <td><span className={"badge badge-" + (RISK_COLOR[e.risk_level] || 'gray')}>{RISK_LABEL[e.risk_level] || e.risk_level || '-'}</span></td>
                        <td><span className="muted">{e.address || '-'}</span></td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                {pagination.total_pages > 1 && (
                  <div style={{display:'flex', alignItems:'center', justifyContent:'center', gap:10, padding:'12px 0', borderTop:'1px solid var(--border-soft)'}}>
                    <button className="btn btn-ghost btn-sm" disabled={pagination.page <= 1}
                      onClick={()=>setPage(p => Math.max(1, p - 1))}>이전</button>
                    <span className="muted" style={{fontSize:12}}>
                      {pagination.page} / {pagination.total_pages} 페이지 · 전체 {pagination.total.toLocaleString()}명
                    </span>
                    <button className="btn btn-ghost btn-sm" disabled={pagination.page >= pagination.total_pages}
                      onClick={()=>setPage(p => Math.min(pagination.total_pages, p + 1))}>다음</button>
                  </div>
                )}
              </div>

              {/* 우측 상세 카드 — management의 매니저 상세 카드와 동일 골격 */}
              <div className="card" style={{overflow:'hidden'}}>
                {!selectedId ? (
                  <div className="muted" style={{padding:30, textAlign:'center'}}>어르신을 선택하세요</div>
                ) : detailLoading || !selected ? (
                  <div className="muted" style={{padding:30, textAlign:'center'}}>불러오는 중…</div>
                ) : (
                  <ElderlyDetailCard detail={selected} canEdit={perms.canManageElderlyRisk} canManageAssessments={perms.canManageAssessments} onRiskChanged={onRiskChanged} onHealthChanged={onHealthChanged}/>
                )}
              </div>
            </div>

          </div>
          <AppFooter/>
        </div>
      </div>

      {creating && (
        <ElderlyCreateModal
          user={user}
          onClose={()=>setCreating(false)}
          onCreated={(d)=>{
            setCreating(false);
            setCreated(d);
            setErr(null);
            // 목록을 다시 받아 정렬/필터 규칙대로 새 행이 제자리에 들어가게 하고,
            // 방금 등록한 어르신을 바로 선택해 우측 상세로 이어지게 한다.
            load();
            if (d?.elderly?.id) setSelectedId(d.elderly.id);
          }}/>
      )}
    </div>
  );
}

// 우측 상세 카드 본문. management의 selected 카드 골격을 그대로 따라간 뒤
// 어르신 도메인의 추가 섹션(복약 / 담당 매니저 / 최근 방문)을 액션 row 아래에 둔다.
function ElderlyDetailCard({ detail, canEdit, canManageAssessments, onRiskChanged, onHealthChanged }) {
  const e  = detail.elderly;
  const am = detail.assigned_manager;
  const uv = detail.upcoming_visit;
  const rb = { color: RISK_COLOR[e.risk_level] || "gray", label: RISK_LABEL[e.risk_level] || e.risk_level };

  const [editingRisk, setEditingRisk] = React.useState(false);
  const [editingHealth, setEditingHealth] = React.useState(false);
  // 욕구사정 — 어르신 상세를 열 때마다 API 로드. 편집/승인 후 재로드로 최신 상태 유지.
  const [assessments, setAssessments] = React.useState(null);
  const [editingAssess, setEditingAssess] = React.useState(null); // null | 'new' | {id, ...}
  const reloadAssessments = React.useCallback(() => {
    needsApi.listForElderly(e.id).then(setAssessments).catch(()=>setAssessments([]));
  }, [e.id]);
  React.useEffect(reloadAssessments, [reloadAssessments]);

  return (
    <>
      <div style={{height:120, background:'linear-gradient(135deg, var(--orange-50), var(--green-50))', display:'grid', placeItems:'center'}}>
        <AvatarImg className="avatar lg" style={{width:64, height:64, fontSize:24}} url={e.avatar_url} name={e.name}/>
      </div>

      <div style={{padding:'18px 22px 8px'}}>
        <div className="h2" style={{fontSize:18}}>
          {e.name} <span className="muted" style={{fontSize:13, fontWeight:500}}>· {e.age != null ? `${e.age}세` : '나이 미상'}</span>
        </div>
        <div style={{display:'flex', alignItems:'center', gap:6, marginTop:4}}>
          <div className="muted" style={{fontSize:12, flex:1, minWidth:0}}>{e.health_status || '건강상태 정보 없음'}</div>
          {canEdit && (
            <button className="btn btn-ghost" style={{padding:'2px 8px', fontSize:10, fontWeight:600, flexShrink:0}} onClick={()=>setEditingHealth(true)}>건강상태 변경</button>
          )}
        </div>
      </div>

      {/* KPI 그리드 — management와 동일하게 2x3 배치 */}
      <div style={{padding:'12px 22px', display:'grid', gridTemplateColumns:'1fr 1fr', gap:10}}>
        {[
          { l: '위험등급',     v: (
            <span style={{display:'inline-flex', alignItems:'center', gap:6}}>
              <span className={"badge badge-" + rb.color}>{rb.label}</span>
              {canEdit && (
                <button className="btn btn-ghost" style={{padding:'2px 8px', fontSize:10, fontWeight:600}} onClick={()=>setEditingRisk(true)}>변경</button>
              )}
            </span>
          ) },
          { l: '나이',         v: e.age != null ? `${e.age}세` : '-' },
          { l: '담당 매니저',  v: am && !am.proposed ? am.name : <span className="muted">미배정</span> },
          { l: '오늘 예정 방문', v: uv ? formatTime(uv.scheduled_at) : <span className="muted">없음</span> },
          { l: '미해결 알림',  v: <span style={{color: (detail.open_alerts ?? 0) > 0 ? 'var(--rose-500)' : 'inherit'}}>{detail.open_alerts ?? 0}</span> },
          { l: '전화',         v: e.phone || '-' },
        ].map((f, i) => (
          <div key={i}>
            <div className="muted" style={{fontSize:10, fontWeight:600}}>{f.l}</div>
            <div style={{fontSize:12.5, fontWeight:600, marginTop:2}}>{f.v}</div>
          </div>
        ))}
      </div>

      {/* 액션 row — management와 동일 위치/스타일 */}
      <div style={{padding:'14px 22px', borderTop:'1px solid var(--border-soft)', display:'flex', gap:6}}>
        {e.lat != null && e.lng != null ? (
          <a className="btn btn-primary btn-md" style={{flex:1, textDecoration:'none', display:'inline-flex', justifyContent:'center'}} href={`/map?elderly_id=${e.id}`}>지도에서 보기</a>
        ) : (
          <button className="btn btn-primary btn-md" disabled style={{flex:1}}>좌표 없음</button>
        )}
        {am && !am.proposed && (
          <a className="btn btn-ghost btn-md" href={`/managers?id=${am.id}`} style={{textDecoration:'none'}}>매니저 프로필</a>
        )}
      </div>

      {/* 어르신 도메인 보조 섹션 — 복약 / 담당 매니저 / 최근 방문.
          management엔 없는 정보지만 운영자 시각에서 필요. KPI/액션 다음에 배치해 시각 일관성 유지. */}
      <div style={{padding:'14px 22px', borderTop:'1px solid var(--border-soft)'}}>

        {/* 주소 */}
        {e.address && (
          <div style={{fontSize:12, color:'var(--ink-2)', marginBottom:10, lineHeight:1.55}}>
            📍 {e.address}
          </div>
        )}

        {/* 복약 */}
        {Array.isArray(e.medications) && e.medications.length > 0 && (
          <div style={{marginBottom:14}}>
            <div className="muted" style={{fontSize:10, fontWeight:700, letterSpacing:'0.04em', marginBottom:6}}>복약</div>
            <div style={{display:'flex', gap:5, flexWrap:'wrap'}}>
              {e.medications.map((m, i) => (
                <span key={i} style={{padding:'3px 9px', fontSize:10.5, fontWeight:600, background:'var(--bg-sub)', borderRadius:99, color:'var(--ink-2)'}}>
                  {m}
                </span>
              ))}
            </div>
          </div>
        )}

        {/* 욕구사정 — 최신 승인 사정 요약 + 최근 3건 이력. 서비스계획·매칭 근거의 원천. */}
        <div style={{marginBottom:14}}>
          <div style={{display:'flex', alignItems:'center', gap:6, marginBottom:6}}>
            <div className="muted" style={{fontSize:10, fontWeight:700, letterSpacing:'0.04em', flex:1}}>욕구사정</div>
            {canManageAssessments && (
              <button className="btn btn-ghost" style={{padding:'2px 8px', fontSize:10, fontWeight:600}}
                onClick={()=>setEditingAssess('new')}>+ 새 사정</button>
            )}
          </div>
          {assessments === null ? (
            <div className="muted" style={{fontSize:11}}>불러오는 중…</div>
          ) : assessments.length === 0 ? (
            <div className="muted" style={{fontSize:11, padding:'6px 0'}}>사정 이력이 없습니다.</div>
          ) : (
            <div style={{display:'flex', flexDirection:'column', gap:5}}>
              {assessments.slice(0, 3).map(a => {
                const rec = a.risk_recommendation;
                const rc  = RISK_COLOR[rec] || 'gray';
                const dateStr = new Date(a.assessed_at).toLocaleDateString('ko-KR');
                return (
                  <div key={a.id}
                    onClick={()=>setEditingAssess(a)}
                    style={{display:'flex', alignItems:'center', gap:8, padding:'8px 10px',
                      background:'var(--bg-sub)', borderRadius:8, cursor:'pointer',
                      borderLeft:`3px solid var(--${rc}-500)`}}>
                    <div style={{flex:1, minWidth:0}}>
                      <div style={{fontSize:11.5, fontWeight:600}}>
                        {dateStr} · 총점 <span className="mono">{a.total_score}/28</span>
                        {a.status === 'draft' && <span className="badge badge-gray" style={{marginLeft:6, fontSize:9}}>초안</span>}
                      </div>
                      <div className="muted" style={{fontSize:10}}>
                        권고 {RISK_LABEL[rec] || rec} · {a.assessor?.name || '사정자 미상'}
                      </div>
                    </div>
                    <span className={"badge badge-" + rc} style={{fontSize:10}}>{RISK_LABEL[rec] || rec}</span>
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {/* 담당 매니저 상세 — proposed(매칭 후보)는 확정 담당이 아니므로 헤더를 구분해
            "담당 매니저"로 오인되지 않게 한다(종결 후 재매칭으로 과거 매니저가 다시
            후보로 제안되는 경우 포함). 확정 배정은 케이스 care_manager 기준. */}
        {am && (
          <div style={{marginBottom:14}}>
            <div className="muted" style={{fontSize:10, fontWeight:700, letterSpacing:'0.04em', marginBottom:6}}>{am.proposed ? '제안 매니저 (매칭 중)' : '담당 매니저'}</div>
            <div style={{padding:'10px 12px', background:'var(--bg-sub)', borderRadius:10}}>
              <div style={{display:'flex', alignItems:'center', gap:10}}>
                <AvatarImg className="avatar sm" style={{background:'linear-gradient(135deg, var(--orange-300), var(--orange-500))', color:'#fff'}} url={am.avatar_url} name={am.name}/>
                <div style={{flex:1, minWidth:0}}>
                  <div style={{fontSize:12.5, fontWeight:600}}>
                    {am.name}
                    {am.proposed && <span className="badge badge-amber" style={{marginLeft:6, fontSize:9}}>제안</span>}
                  </div>
                  <div className="muted" style={{fontSize:10}}>
                    {am.qualification || ''} · {am.organization_name || '소속 미배정'}
                  </div>
                </div>
                <span className={"badge badge-" + (STATUS_COLOR[am.status] || 'gray')}>{STATUS_LABEL[am.status] || am.status}</span>
              </div>
              {(am.phone || am.rating != null) && (
                <div style={{marginTop:8, display:'flex', justifyContent:'space-between', fontSize:11, color:'var(--ink-2)'}}>
                  {am.phone && <span>📞 {am.phone}</span>}
                  {am.rating != null && <span className="mono">★ {Number(am.rating).toFixed(1)}</span>}
                </div>
              )}
            </div>
          </div>
        )}

        {/* 최근 방문 */}
        <div className="muted" style={{fontSize:10, fontWeight:700, letterSpacing:'0.04em', marginBottom:6}}>최근 방문</div>
        {detail.recent_visits.length === 0 ? (
          <div className="muted" style={{padding:'8px 0', fontSize:11.5}}>방문 기록이 없습니다.</div>
        ) : (
          detail.recent_visits.map((v, i) => (
            <div key={v.id} style={{
              display:'flex', alignItems:'center', gap:8, padding:'8px 0',
              borderTop: i > 0 ? '1px solid var(--border-soft)' : 'none',
            }}>
              <div style={{flex:1, minWidth:0}}>
                <div style={{fontSize:12, fontWeight:600}}>{formatDateTime(v.scheduled_at)}</div>
                <div className="muted" style={{fontSize:10}}>{v.manager_name || '매니저 미배정'}</div>
              </div>
              <span className={"badge badge-" + (VISIT_COLOR[v.status] || 'gray')}>{VISIT_LABEL[v.status] || v.status}</span>
            </div>
          ))
        )}
      </div>

      {editingRisk && (
        <RiskEditModal
          elderly={e}
          onClose={()=>setEditingRisk(false)}
          onSaved={(updated)=>{ onRiskChanged?.(updated); setEditingRisk(false); }}
        />
      )}

      {editingHealth && (
        <HealthEditModal
          elderly={e}
          onClose={()=>setEditingHealth(false)}
          onSaved={(updated)=>{ onHealthChanged?.(updated); setEditingHealth(false); }}
        />
      )}

      {editingAssess && (
        <NeedsAssessmentModal
          elderly={e}
          initial={editingAssess === 'new' ? null : editingAssess}
          onClose={()=>setEditingAssess(null)}
          onSaved={()=>{ setEditingAssess(null); reloadAssessments(); }}
        />
      )}
    </>
  );
}

// 건강상태 변경 모달 — 자유서술 + 사유 필수. 저장 성공 시 onSaved(갱신 payload).
// 위험도 모달과 동일 골격. 서버가 reason 누락/권한/검증을 재확인하므로 클라이언트는 UX 가드만.
function HealthEditModal({ elderly, onClose, onSaved }) {
  const [health, setHealth] = React.useState(elderly.health_status || "");
  const [reason, setReason] = React.useState("");
  const [saving, setSaving] = React.useState(false);
  const [error, setError] = React.useState(null);

  const unchanged = (health.trim()) === (elderly.health_status || "").trim();
  const canSave = !unchanged && reason.trim().length > 0 && !saving;

  const save = async () => {
    if (!canSave) return;
    setSaving(true); setError(null);
    try {
      const updated = await elderlyApi.updateHealth(elderly.id, health.trim(), reason.trim());
      onSaved(updated);
    } catch (err) {
      setError(humanizeError(err));
      setSaving(false);
    }
  };

  return (
    <div onClick={onClose} style={{position:'fixed', inset:0, background:'rgba(0,0,0,0.4)', display:'grid', placeItems:'center', zIndex:1000}}>
      <div onClick={e=>e.stopPropagation()} className="card" style={{width:400, maxWidth:'92vw', padding:20}}>
        <div className="h2" style={{fontSize:16, marginBottom:4}}>건강상태 변경</div>
        <div className="muted" style={{fontSize:12, marginBottom:14}}>{elderly.name}</div>

        <label className="muted" style={{fontSize:11, fontWeight:600, display:'block', marginBottom:4}}>건강상태</label>
        <textarea className="input" rows={3} value={health} onChange={e=>setHealth(e.target.value)}
          placeholder="예: 고혈압·당뇨, 거동 불편" style={{width:'100%', resize:'vertical', marginBottom:12}}/>

        <label className="muted" style={{fontSize:11, fontWeight:600, display:'block', marginBottom:4}}>변경 사유 (필수)</label>
        <textarea className="input" rows={3} value={reason} onChange={e=>setReason(e.target.value)}
          placeholder="예: 최근 방문 시 보행 보조 필요 확인" style={{width:'100%', resize:'vertical', marginBottom:12}}/>

        {error && <div style={{color:'var(--rose-500)', fontSize:12, fontWeight:600, marginBottom:10}}>{error}</div>}

        <div style={{display:'flex', gap:8, justifyContent:'flex-end'}}>
          <button className="btn btn-ghost btn-md" onClick={onClose} disabled={saving}>취소</button>
          <button className="btn btn-primary btn-md" onClick={save} disabled={!canSave}>{saving ? "저장 중…" : "저장"}</button>
        </div>
      </div>
    </div>
  );
}

// 위험등급 변경 모달 — 위험/주의/안정 선택 + 사유 필수. 저장 성공 시 onSaved(갱신 payload).
// 서버가 reason 누락/권한/검증을 재확인하므로 클라이언트는 UX 가드만 수행.
function RiskEditModal({ elderly, onClose, onSaved }) {
  const [level, setLevel] = React.useState(elderly.risk_level);
  const [reason, setReason] = React.useState("");
  const [saving, setSaving] = React.useState(false);
  const [error, setError] = React.useState(null);

  const unchanged = level === elderly.risk_level;
  const canSave = !unchanged && reason.trim().length > 0 && !saving;

  const save = async () => {
    if (!canSave) return;
    setSaving(true); setError(null);
    try {
      const updated = await elderlyApi.updateRisk(elderly.id, level, reason.trim());
      onSaved(updated);
    } catch (err) {
      setError(humanizeError(err));
      setSaving(false);
    }
  };

  return (
    <div onClick={onClose} style={{position:'fixed', inset:0, background:'rgba(0,0,0,0.4)', display:'grid', placeItems:'center', zIndex:1000}}>
      <div onClick={e=>e.stopPropagation()} className="card" style={{width:360, maxWidth:'92vw', padding:20}}>
        <div className="h2" style={{fontSize:16, marginBottom:4}}>위험등급 변경</div>
        <div className="muted" style={{fontSize:12, marginBottom:14}}>{elderly.name} · 현재 {RISK_LABEL[elderly.risk_level] || elderly.risk_level}</div>

        <div style={{display:'flex', gap:8, marginBottom:14}}>
          {["critical","warning","stable"].map((lv)=>(
            <button key={lv} onClick={()=>setLevel(lv)}
              className={"btn btn-md " + (level===lv ? "btn-primary" : "btn-ghost")}
              style={{flex:1}}>{RISK_LABEL[lv]}</button>
          ))}
        </div>

        <label className="muted" style={{fontSize:11, fontWeight:600, display:'block', marginBottom:4}}>변경 사유 (필수)</label>
        <textarea className="input" rows={3} value={reason} onChange={e=>setReason(e.target.value)}
          placeholder="예: 최근 방문 시 낙상 위험 증가 확인" style={{width:'100%', resize:'vertical', marginBottom:12}}/>

        {error && <div style={{color:'var(--rose-500)', fontSize:12, fontWeight:600, marginBottom:10}}>{error}</div>}

        <div style={{display:'flex', gap:8, justifyContent:'flex-end'}}>
          <button className="btn btn-ghost btn-md" onClick={onClose} disabled={saving}>취소</button>
          <button className="btn btn-primary btn-md" onClick={save} disabled={!canSave}>{saving ? "저장 중…" : "저장"}</button>
        </div>
      </div>
    </div>
  );
}

// ── 헬퍼 ─────────────────────────────────────────────────────────────

function formatDateTime(iso) {
  if (!iso) return '-';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return `${d.getMonth()+1}월 ${d.getDate()}일 ${pad(d.getHours())}:${pad(d.getMinutes())}`;
}
function formatTime(iso) {
  if (!iso) return '-';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return `${pad(d.getHours())}:${pad(d.getMinutes())}`;
}
function pad(n) { return String(n).padStart(2, '0'); }

function humanizeError(e) {
  return apiErrorMessage(e);
}

// 통합돌봄 욕구사정 편집 모달 — 7 도메인(0~4점) 라디오 + 자유메모.
// 초안 저장(status=draft)과 승인(status=approved) 두 액션을 제공.
// 승인된 사정은 서버가 편집을 잠그므로(controller의 locked 응답), 이 화면은
// initial.status==='approved' 일 때 읽기전용으로 표시한다.
const NA_DOMAINS = [
  { k: "adl_score",       l: "일상생활 수행능력(ADL)", h: "식사/이동/화장실/옷입기/씻기 등 기본 활동" },
  { k: "iadl_score",      l: "수단적 일상생활(IADL)",  h: "가사/이동수단/약복용/재무관리" },
  { k: "cognitive_score", l: "인지 기능",              h: "기억/판단/의사소통" },
  { k: "social_score",    l: "사회 참여·관계",          h: "고립/외출/관계망" },
  { k: "housing_score",   l: "주거 환경",              h: "안전/난방/청결/위험요소" },
  { k: "nutrition_score", l: "영양 상태",              h: "체중/식사규칙/영양불량 징후" },
  { k: "medical_score",   l: "의료 필요도",            h: "만성질환/투약/처치·간호 필요" },
];
const NA_LEVELS = [
  { v: 0, l: "자립" },
  { v: 1, l: "경도 도움" },
  { v: 2, l: "중등도" },
  { v: 3, l: "고도 도움" },
  { v: 4, l: "완전 의존" },
];

function NeedsAssessmentModal({ elderly, initial, onClose, onSaved }) {
  const readOnly = initial?.status === "approved";
  const [scores, setScores] = React.useState(() => {
    const s = initial?.scores || {};
    return Object.fromEntries(NA_DOMAINS.map(d => [d.k, Number(s[d.k.replace(/_score$/, "")] ?? 0)]));
  });
  const [notes, setNotes] = React.useState(initial?.notes || "");
  const [saving, setSaving] = React.useState(false);
  const [err, setErr] = React.useState(null);

  const total = Object.values(scores).reduce((a, b) => a + Number(b || 0), 0);
  const maxDomain = Math.max(...Object.values(scores).map(v => Number(v || 0)));
  const recommend = total >= 20 || maxDomain >= 4 ? "critical"
                  : total >= 10 || maxDomain >= 3 ? "warning" : "stable";
  const rc = RISK_COLOR[recommend] || "gray";

  const save = async (approve) => {
    setSaving(true); setErr(null);
    try {
      const payload = { ...scores, notes };
      let saved;
      if (initial?.id) {
        saved = await needsApi.update(initial.id, payload);
        if (approve) saved = await needsApi.approve(initial.id);
      } else {
        saved = await needsApi.create(elderly.id, payload);
        if (approve) saved = await needsApi.approve(saved.id);
      }
      onSaved(saved);
    } catch (e) { setErr(apiErrorMessage(e)); setSaving(false); }
  };

  return (
    <div onClick={onClose}
      style={{position:'fixed', inset:0, background:'rgba(20,16,12,0.4)', display:'grid', placeItems:'center', zIndex:60}}>
      <div onClick={e=>e.stopPropagation()} className="card"
        style={{width:640, maxHeight:'90vh', overflow:'auto', padding:0}}>
        <div style={{padding:'16px 22px', borderBottom:'1px solid var(--border-soft)',
          display:'flex', alignItems:'center', justifyContent:'space-between'}}>
          <div>
            <div className="h3">욕구사정 · {elderly.name}</div>
            <div className="muted" style={{fontSize:11, marginTop:2}}>
              {readOnly
                ? `승인됨 · ${new Date(initial.assessed_at).toLocaleString('ko-KR')}`
                : "0(자립) ~ 4(완전 의존) · 총점 20 이상 또는 4점 도메인 존재 시 위험 권고"}
            </div>
          </div>
          <button className="btn btn-ghost btn-sm" onClick={onClose}>{Ic.close}</button>
        </div>

        {err && (
          <div style={{margin:'14px 22px 0', padding:'10px 14px', background:'var(--rose-50)',
            color:'var(--rose-500)', borderRadius:10, fontSize:12.5}}>{err}</div>
        )}

        <div style={{padding:'18px 22px', display:'flex', flexDirection:'column', gap:14}}>
          {NA_DOMAINS.map(d => (
            <div key={d.k}>
              <div style={{display:'flex', alignItems:'baseline', gap:8, marginBottom:6}}>
                <div style={{fontSize:12.5, fontWeight:700, color:'var(--ink-1)'}}>{d.l}</div>
                <div className="muted" style={{fontSize:10.5}}>{d.h}</div>
                <div style={{flex:1}}/>
                <div className="mono" style={{fontSize:11, fontWeight:700, color:'var(--ink-2)'}}>{scores[d.k]}점</div>
              </div>
              <div style={{display:'flex', gap:4}}>
                {NA_LEVELS.map(lv => {
                  const active = scores[d.k] === lv.v;
                  return (
                    <button key={lv.v}
                      onClick={()=>!readOnly && setScores(s => ({ ...s, [d.k]: lv.v }))}
                      disabled={readOnly}
                      style={{
                        flex:1, padding:'8px 4px', fontSize:10.5, fontWeight:600,
                        border:`1px solid ${active ? 'var(--orange-500)' : 'var(--border)'}`,
                        background: active ? 'var(--orange-50)' : '#fff',
                        color: active ? 'var(--orange-600)' : 'var(--ink-2)',
                        borderRadius:6, cursor: readOnly ? 'not-allowed' : 'pointer',
                      }}>
                      <div>{lv.v}</div>
                      <div style={{fontSize:9, marginTop:1}}>{lv.l}</div>
                    </button>
                  );
                })}
              </div>
            </div>
          ))}

          <div>
            <div className="muted" style={{fontSize:10.5, fontWeight:700, marginBottom:4}}>메모</div>
            <textarea value={notes} onChange={e=>setNotes(e.target.value)} disabled={readOnly}
              rows={3} className="input" style={{width:'100%', fontFamily:'inherit', fontSize:12}}
              placeholder="관찰 사항, 특이사항, 개별 문항 근거 등"/>
          </div>

          <div style={{padding:'10px 12px', background:'var(--bg-sub)', borderRadius:8,
            display:'flex', alignItems:'center', gap:12}}>
            <div style={{flex:1}}>
              <div className="muted" style={{fontSize:10}}>총점 / 자동 위험 권고</div>
              <div style={{fontSize:13, fontWeight:700, marginTop:2}}>
                <span className="mono">{total}/28</span>
                <span style={{marginLeft:10}}>
                  <span className={"badge badge-" + rc}>{RISK_LABEL[recommend]}</span>
                </span>
              </div>
            </div>
          </div>
        </div>

        <div style={{padding:'14px 22px', borderTop:'1px solid var(--border-soft)',
          display:'flex', justifyContent:'flex-end', gap:8}}>
          <button className="btn btn-ghost btn-md" onClick={onClose} disabled={saving}>닫기</button>
          {!readOnly && (
            <>
              <button className="btn btn-ghost btn-md" onClick={()=>save(false)} disabled={saving}>
                {saving ? "저장 중…" : "초안 저장"}
              </button>
              <button className="btn btn-primary btn-md" onClick={()=>save(true)} disabled={saving}>
                {saving ? "저장 중…" : "저장·승인"}
              </button>
            </>
          )}
        </div>
      </div>
    </div>
  );
}

export { ElderlyDetail };
